export function toggleBold(s: string): string {
  if (isBold(s)) {
    return makeNotBold(s);
  } else {
    return makeBold(s);
  }
}

export function toggleItalic(s: string): string {
  if (isItalic(s)) {
    return makeNotItalic(s);
  } else {
    return makeItalic(s);
  }
}

export function isBold(s: string): boolean {
  return (
    !isWhitespaceOrPunctuation(s) &&
    Array.from(s).every(
      char =>
        charIsInVariant(char, "bold") ||
        charIsInVariant(char, "boldItalic") ||
        charIsWhitespace(char) ||
        charIsPunctuation(char)
    )
  );
}

export function isItalic(s: string): boolean {
  return (
    !isWhitespaceOrPunctuation(s) &&
    Array.from(s).every(
      char =>
        charIsInVariant(char, "italic") ||
        charIsInVariant(char, "boldItalic") ||
        charIsWhitespace(char) ||
        charIsPunctuation(char)
    )
  );
}

export function stripFonts(s: string): string {
  return Array.from(s)
    .map(char => {
      for (const variant of ["bold", "italic", "boldItalic"] as Variant[]) {
        if (charIsInVariant(char, variant)) {
          return convertChar(char, variant, "regular");
        }
      }
      return char;
    })
    .join("");
}

function isWhitespaceOrPunctuation(s: string) {
  return Array.from(s).every(
    char => charIsWhitespace(char) || charIsPunctuation(char)
  );
}

function makeBold(s: string): string {
  return Array.from(s)
    .map(char => {
      if (isWhitespaceOrPunctuation(char) || isDiacritic(char)) {
        return char;
      }
      if (charIsInVariant(char, "regular")) {
        return convertChar(char, "regular", "bold");
      }
      if (charIsInVariant(char, "italic")) {
        return convertChar(char, "italic", "boldItalic");
      }
      return char;
    })
    .join("");
}

function makeNotBold(s: string): string {
  return Array.from(s)
    .map(char => {
      if (isWhitespaceOrPunctuation(char) || isDiacritic(char)) {
        return char;
      }
      if (charIsInVariant(char, "bold")) {
        return convertChar(char, "bold", "regular");
      }
      if (charIsInVariant(char, "boldItalic")) {
        return convertChar(char, "boldItalic", "italic");
      }
      return char;
    })
    .join("");
}

function makeItalic(s: string) {
  return Array.from(s)
    .map(char => {
      if (isWhitespaceOrPunctuation(char) || isDiacritic(char)) {
        return char;
      }
      if (charIsInVariant(char, "regular")) {
        return convertChar(char, "regular", "italic");
      }
      if (charIsInVariant(char, "bold")) {
        return convertChar(char, "bold", "boldItalic");
      }
      return char;
    })
    .join("");
}

function makeNotItalic(s: string) {
  return Array.from(s)
    .map(char => {
      if (isWhitespaceOrPunctuation(char) || isDiacritic(char)) {
        return char;
      }
      if (charIsInVariant(char, "italic")) {
        return convertChar(char, "italic", "regular");
      }
      if (charIsInVariant(char, "boldItalic")) {
        return convertChar(char, "boldItalic", "bold");
      }
      return char;
    })
    .join("");
}

type Variant = "regular" | "bold" | "italic" | "boldItalic";

function charIsWhitespace(char: string) {
  return /^\s*$/.test(char);
}

function charIsPunctuation(char: string) {
  return /^[!"“”#$%&'‘’()*+,-./:;<=>?@[\\\]^_`{|}~]$/.test(char);
}

function charIsInVariant(char: string, variant: Variant) {
  return (
    isSpecialCharacter(char, variant) ||
    isUppercaseLetter(char, variant) ||
    isLowercaseLetter(char, variant) ||
    isDigit(char, variant) ||
    isDiacritic(char)
  );
}

// This is adapted from https://github.com/davidkonrad/toUnicodeVariant
function convertChar(char: string, from: Variant, to: Variant) {
  let index;
  let c = convertToRegular(char, from);
  const combineSpecial = c in SPECIAL_CHARS ? SPECIAL_CHARS[c].combine : false;
  c = combineSpecial
    ? SPECIAL_CHARS[c].char
    : c.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (SPECIAL[to][c]) {
    c = String.fromCodePoint(SPECIAL[to][c]);
  }
  if ((index = UPPERCASE_LETTERS.indexOf(c)) > -1) {
    c = String.fromCodePoint(index + OFFSETS[to].uppercaseLetterOffset);
  } else if ((index = LOWERCASE_LETTERS.indexOf(c)) > -1) {
    c = String.fromCodePoint(index + OFFSETS[to].lowercaseLetterOffset);
  } else if ((index = DIGITS.indexOf(c)) > -1 && OFFSETS[to].digitOffset) {
    c = String.fromCodePoint(index + OFFSETS[to].digitOffset!);
  }
  if (combineSpecial) {
    c += combineSpecial;
  }
  return c;
}

function convertToRegular(char: string, from: Variant) {
  const codePoint = char.codePointAt(0);
  if (!codePoint) {
    return char;
  }
  if (isSpecialCharacter(char, from)) {
    // This will strip out diacritics
    // Probably not a huge deal because it only applies to italic h
    return (
      Object.keys(SPECIAL[from]).find(
        key =>
          SPECIAL[from][key] ===
          char
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .codePointAt(0)
      ) ?? char
    );
  }
  if (isUppercaseLetter(char, from)) {
    return String.fromCodePoint(
      codePoint -
        OFFSETS[from].uppercaseLetterOffset +
        OFFSETS.regular.uppercaseLetterOffset
    );
  }
  if (isLowercaseLetter(char, from)) {
    return String.fromCodePoint(
      codePoint -
        OFFSETS[from].lowercaseLetterOffset +
        OFFSETS.regular.lowercaseLetterOffset
    );
  }
  if (OFFSETS[from].digitOffset && isDigit(char, from)) {
    return String.fromCodePoint(
      codePoint - OFFSETS[from].digitOffset! + OFFSETS.regular.digitOffset!
    );
  }
  return char;
}

function isSpecialCharacter(char: string, variant: Variant) {
  const codePoint = char
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .codePointAt(0);
  if (!codePoint) {
    return false;
  }
  return Object.values(SPECIAL[variant]).includes(codePoint);
}

function isUppercaseLetter(char: string, variant: Variant) {
  const codePoint = char
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .codePointAt(0);
  if (!codePoint) {
    return false;
  }
  return (
    codePoint - OFFSETS[variant].uppercaseLetterOffset > -1 &&
    codePoint - OFFSETS[variant].uppercaseLetterOffset <
      UPPERCASE_LETTERS.length
  );
}

function isLowercaseLetter(char: string, variant: Variant) {
  const codePoint = char
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .codePointAt(0);
  if (!codePoint) {
    return false;
  }
  return (
    codePoint - OFFSETS[variant].lowercaseLetterOffset > -1 &&
    codePoint - OFFSETS[variant].lowercaseLetterOffset <
      LOWERCASE_LETTERS.length
  );
}

function isDigit(char: string, variant: Variant) {
  const codePoint = char
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .codePointAt(0);
  if (!codePoint) {
    return false;
  }
  return (
    codePoint - OFFSETS[variant].digitOffset! > -1 &&
    codePoint - OFFSETS[variant].digitOffset! < DIGITS.length
  );
}

function isDiacritic(char: string) {
  return char.normalize("NFD").replace(/[\u0300-\u036f]/g, "").length === 0;
}

const OFFSETS: {
  [v in Variant]: {
    uppercaseLetterOffset: number;
    lowercaseLetterOffset: number;
    digitOffset?: number;
  };
} = {
  regular: {
    uppercaseLetterOffset: 0x00041,
    lowercaseLetterOffset: 0x00061,
    digitOffset: 0x00030,
  },
  bold: {
    uppercaseLetterOffset: 0x1d5d4,
    lowercaseLetterOffset: 0x1d5d4 + 26,
    digitOffset: 0x1d7ec,
  },
  italic: {
    uppercaseLetterOffset: 0x1d608,
    lowercaseLetterOffset: 0x1d608 + 26,
  },
  boldItalic: {
    uppercaseLetterOffset: 0x1d63c,
    lowercaseLetterOffset: 0x1d63c + 26,
  },
};

const SPECIAL: { [v in Variant]: { [c: string]: number } } = {
  regular: {},
  bold: {},
  italic: {},
  boldItalic: {},
};

const DIACRITICS = {
  strike: { short: "s", code: 0x0336 },
  "strike-curly": { short: "sc", code: 0x0334 },
  underline: { short: "u", code: 0x0332 },
  "underline-curly": { short: "uc", code: 0x0330 },
  "underline-sm": { short: "u-sm", code: 0x0320 },
  "underline-double": { short: "ud", code: 0x0333 },
  "underline-double-sm": { short: "ud-sm", code: 0x0347 },
  overline: { short: "o", code: 0x0305 },
  "overline-curly": { short: "oc", code: 0x0303 },
  "overline-sm": { short: "o-sm", code: 0x0304 },
  "overline-double": { short: "od", code: 0x033f },
  slash: { short: "sl", code: 0x0338 },
  "cross-above": { short: "ca", code: 0x033d },
  "plus-below": { short: "pb", code: 0x031f },
  "a-above": { short: "a-a", code: 0x0363 },
  "c-above": { short: "c-a", code: 0x0368 },
  "d-above": { short: "d-a", code: 0x0369 },
  "e-above": { short: "e-a", code: 0x0364 },
  "h-above": { short: "h-a", code: 0x036a },
  "i-above": { short: "i-a", code: 0x0365 },
  "m-above": { short: "m-a", code: 0x036b },
  "o-above": { short: "o-a", code: 0x0366 },
  "r-above": { short: "r-a", code: 0x036c },
  "u-above": { short: "u-a", code: 0x0367 },
  "v-above": { short: "v-a", code: 0x036e },
  "x-above": { short: "x-a", code: 0x036f },
  "halo-breve": { short: "hb", code: 0x0488 },
  "halo-grave": { short: "hg", code: 0x0489 },
  "enclose-circle": { short: "en-c", code: 0x20dd },
  "enclose-backslash": { short: "en-cb", code: 0x20e0 },
  "enclose-circle-backslash": { short: "en-cb", code: 0x20e0 },
  "enclose-square": { short: "en-s", code: 0x20de },
  "enclose-diamond": { short: "en-d", code: 0x20df },
  "enclose-screen": { short: "en-scr", code: 0x20e2 },
  "enclose-keycap": { short: "en-key", code: 0x20e3 },
  //diacritics supporting special chars
  diaeresis: { code: 0x0308 },
  caron: { code: 0x030c },
  perispomeni: { code: 0x0342 },
  tilde: { code: 0x0303 },
  tildesm: { code: 0x02dc },
  circumflex: { code: 0x0302 },
  ringabove: { code: 0x030a },
  dotabove: { code: 0x0307 },
  dotbelow: { code: 0x0323 },
  grave: { code: 0x0340 },
  gravedouble: { code: 0x030f },
  acute: { code: 0x0341 },
  breve: { code: 0x0306 },
  breveinverted: { code: 0x0311 },
  commabelow: { code: 0x0326 },
  macron: { code: 0x0304 },
  cedilla: { code: 0x0327 },
  ogonek: { code: 0x0328 },
  solidus: { code: 0x0338 },
  solidussm: { code: 0x0337 },
  hookabove: { code: 0x0309 },
  horn: { code: 0x031b },
  //spacing combinings
  "space-zero": { code: 0xfeff },
  "space-hair": { code: 0x200a },
  "space-thin": { code: 0x2009 },
  space: { code: 0x0020 },
  "space-en": { code: 0x2000 },
  "space-figure": { code: 0x2007 },
  "space-cjk": { code: 0x3000 },
  "space-em": { code: 0x2001 },
  "space-ogham": { code: 0x1680 },
  //combining grapheme joiner
  CGJ: { code: 0x034f },
};

const SPECIAL_CHARS: {
  [key: string]: { char: string; combine: string | false };
} = {
  ä: { char: "a", combine: String.fromCodePoint(DIACRITICS.diaeresis.code) },
  â: { char: "a", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  á: { char: "a", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  å: { char: "a", combine: String.fromCodePoint(DIACRITICS.ringabove.code) },
  ă: { char: "a", combine: String.fromCodePoint(DIACRITICS.breve.code) },
  ǟ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.diaeresis.code) +
      String.fromCodePoint(DIACRITICS.macron.code),
  },
  ã: { char: "a", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ā: { char: "a", combine: String.fromCodePoint(DIACRITICS.macron.code) },
  ȧ: { char: "a", combine: String.fromCodePoint(DIACRITICS.dotabove.code) },
  ȃ: {
    char: "a",
    combine: String.fromCodePoint(DIACRITICS.breveinverted.code),
  },
  ḅ: { char: "b", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  č: { char: "c", combine: String.fromCodePoint(DIACRITICS.caron.code) },
  ć: { char: "c", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ç: { char: "c", combine: String.fromCodePoint(DIACRITICS.cedilla.code) },
  ḉ: {
    char: "c",
    combine:
      String.fromCodePoint(DIACRITICS.cedilla.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ċ: { char: "c", combine: String.fromCodePoint(DIACRITICS.dotabove.code) },
  ĉ: { char: "c", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  è: { char: "e", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  é: { char: "e", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ē: { char: "e", combine: String.fromCodePoint(DIACRITICS.macron.code) },
  ĕ: { char: "e", combine: String.fromCodePoint(DIACRITICS.breve.code) },
  ë: { char: "e", combine: String.fromCodePoint(DIACRITICS.diaeresis.code) },
  ě: { char: "e", combine: String.fromCodePoint(DIACRITICS.caron.code) },
  ę: { char: "e", combine: String.fromCodePoint(DIACRITICS.ogonek.code) },
  ȇ: {
    char: "e",
    combine: String.fromCodePoint(DIACRITICS.breveinverted.code),
  },
  ȅ: {
    char: "e",
    combine: String.fromCodePoint(DIACRITICS.gravedouble.code),
  },
  ê: { char: "e", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  ğ: { char: "g", combine: String.fromCodePoint(DIACRITICS.breve.code) },
  ǧ: { char: "g", combine: String.fromCodePoint(DIACRITICS.caron.code) },
  ģ: { char: "g", combine: String.fromCodePoint(DIACRITICS.cedilla.code) },
  ġ: { char: "g", combine: String.fromCodePoint(DIACRITICS.dotabove.code) },
  ḥ: { char: "h", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  î: { char: "i", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  í: { char: "i", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ì: { char: "i", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ĩ: { char: "i", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ḱ: { char: "k", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ḳ: { char: "k", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ņ: { char: "n", combine: String.fromCodePoint(DIACRITICS.ogonek.code) },
  ń: { char: "n", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  õ: { char: "o", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ö: { char: "o", combine: String.fromCodePoint(DIACRITICS.diaeresis.code) },
  ō: { char: "o", combine: String.fromCodePoint(DIACRITICS.macron.code) },
  ô: { char: "o", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  ó: { char: "o", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ò: { char: "o", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ŕ: { char: "r", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ş: { char: "s", combine: String.fromCodePoint(DIACRITICS.cedilla.code) },
  ș: { char: "s", combine: String.fromCodePoint(DIACRITICS.commabelow.code) },
  ṩ: {
    char: "s",
    combine:
      String.fromCodePoint(DIACRITICS.dotbelow.code) +
      String.fromCodePoint(DIACRITICS.dotabove.code),
  },
  š: { char: "s", combine: String.fromCodePoint(DIACRITICS.caron.code) },
  ś: { char: "s", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ü: { char: "u", combine: String.fromCodePoint(DIACRITICS.diaeresis.code) },
  ù: { char: "u", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ú: { char: "u", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  û: { char: "u", combine: String.fromCodePoint(DIACRITICS.circumflex.code) },
  ŭ: { char: "u", combine: String.fromCodePoint(DIACRITICS.breve.code) },
  ȕ: {
    char: "u",
    combine: String.fromCodePoint(DIACRITICS.gravedouble.code),
  },
  ȗ: {
    char: "u",
    combine: String.fromCodePoint(DIACRITICS.breveinverted.code),
  },
  ů: { char: "u", combine: String.fromCodePoint(DIACRITICS.ringabove.code) },
  ū: { char: "u", combine: String.fromCodePoint(DIACRITICS.macron.code) },
  ẁ: { char: "w", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ẃ: { char: "w", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ø: { char: "o", combine: String.fromCodePoint(DIACRITICS.solidussm.code) },
  đ: { char: "d", combine: String.fromCodePoint(DIACRITICS.macron.code) },
  à: { char: "a", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ả: { char: "a", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ạ: { char: "a", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ẻ: { char: "e", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ẽ: { char: "e", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ẹ: { char: "e", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ỉ: { char: "i", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ị: { char: "i", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ỏ: { char: "o", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ọ: { char: "o", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ủ: { char: "u", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ũ: { char: "u", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ụ: { char: "u", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ý: { char: "y", combine: String.fromCodePoint(DIACRITICS.acute.code) },
  ỳ: { char: "y", combine: String.fromCodePoint(DIACRITICS.grave.code) },
  ỷ: { char: "y", combine: String.fromCodePoint(DIACRITICS.hookabove.code) },
  ỹ: { char: "y", combine: String.fromCodePoint(DIACRITICS.tilde.code) },
  ỵ: { char: "y", combine: String.fromCodePoint(DIACRITICS.dotbelow.code) },
  ắ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.breve.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ằ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.breve.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ẳ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.breve.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ẵ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.breve.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ặ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.breve.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
  ấ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ầ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ẩ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ẫ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ậ: {
    char: "a",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
  ế: {
    char: "e",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ề: {
    char: "e",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ể: {
    char: "e",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ễ: {
    char: "e",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ệ: {
    char: "e",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
  ố: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ồ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ổ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ỗ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ộ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.circumflex.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
  ơ: { char: "o", combine: String.fromCodePoint(DIACRITICS.horn.code) },
  ớ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ờ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ở: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ỡ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ợ: {
    char: "o",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
  ư: { char: "u", combine: String.fromCodePoint(DIACRITICS.horn.code) },
  ứ: {
    char: "u",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.acute.code),
  },
  ừ: {
    char: "u",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.grave.code),
  },
  ử: {
    char: "u",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.hookabove.code),
  },
  ữ: {
    char: "u",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.tilde.code),
  },
  ự: {
    char: "u",
    combine:
      String.fromCodePoint(DIACRITICS.horn.code) +
      String.fromCodePoint(DIACRITICS.dotbelow.code),
  },
};

// Strip diacritics from uppercase letters
for (const char of Object.keys(SPECIAL_CHARS)) {
  SPECIAL_CHARS[char.toUpperCase()] = { char: char, combine: false };
}

const UPPERCASE_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE_LETTERS = "abcdefghijklmnopqrstuvwxyz";
const DIGITS = "0123456789";
